import React, { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { faCircle as Circle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "../../utils/toast";

import { api } from "../../api/api";
import LetterDialog, {
  LetterDialogActions,
  letterModalBodyClass,
} from "../UX/LetterDialog";
import stamp from "../../assets/icons/post-stamp.svg";

const PRICE_PER_STAMP = 12;

function BuyStampsModal({ open = true, onClose, token }) {
  const [numberOfStamps, setNumberOfStamps] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const checkoutHandler = async () => {
    setIsLoading(true);
    try {
      const response = await api.post(
        "/checkout/create",
        { numberOfStamps, pricePerStamp: PRICE_PER_STAMP },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const checkoutUrl = response.data?.url || response.data?.session?.url;
      if (!checkoutUrl) {
        throw new Error("Missing Stripe Checkout URL");
      }
      window.location.assign(checkoutUrl);
    } catch {
      toast.error("Quelque chose s'est mal passé, veuillez réessayer");
      setIsLoading(false);
    }
  };

  const featurePoints = [
    {
      title: "Valable à vie :",
      description:
        "Ton timbre et le destinataire qui lui est associé restent valables indéfiniment.",
    },
    {
      title: "Sans frais supplémentaires :",
      description:
        "Aucun coût supplémentaire n'est nécessaire pour l'envoi de ta lettre.",
    },
    {
      title: "Modifiable à volonté :",
      description:
        "Tu peux modifier le destinataire associé à ton timbre autant de fois que tu le souhaites.",
    },
    {
      title: "Récupérable :",
      description:
        "Si tu supprimes le destinataire, tu récupères automatiquement le timbre qui lui était associé.",
    },
  ];

  return (
    <LetterDialog
      open={open}
      onClose={onClose}
      maxWidth="max-w-lg"
      scrollable
      animationVariant="scaleFade"
      title="Acheter des timbres"
      footer={
        <LetterDialogActions
          secondaryLabel="Fermer"
          onSecondary={onClose}
          secondaryVariant="outline"
          primaryLabel={isLoading ? "Chargement…" : "Acheter"}
          onPrimary={checkoutHandler}
          primaryVariant="purple"
          primaryDisabled={isLoading}
        />
      }
    >
      <div className="mb-6 flex items-start gap-3">
        <img
          src={stamp}
          className="mt-0.5 h-10 w-10 shrink-0 self-start"
          alt=""
        />
        <p className={`${letterModalBodyClass} mb-0`}>
          Les timbres te permettent d&apos;ajouter des destinataires à tes
          lettres. Elles leur seront envoyées au moment que tu auras choisi.
        </p>
      </div>

      <ul className="mb-6 space-y-4">
        {featurePoints.map((point, index) => (
          <li key={index} className="flex items-start gap-3">
            <FontAwesomeIcon
              icon={Circle}
              className="mt-2 h-1 w-1 shrink-0 fill-current text-primaryText"
            />
            <p className="font-josefin text-sm text-secondaryText">
              <span className="font-bold">{point.title}</span>{" "}
              {point.description}
            </p>
          </li>
        ))}
      </ul>

      <div className="mb-2 flex flex-col items-center">
        <div className="flex items-center gap-4">
          <button
            type="button"
            aria-label="Retirer un timbre"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-primaryText text-primaryText transition hover:bg-primaryText/5"
            onClick={() => numberOfStamps > 1 && setNumberOfStamps((n) => n - 1)}
          >
            <Minus size={16} strokeWidth={2.5} />
          </button>
          <span className="min-w-[2ch] text-center font-josefin text-2xl text-secondaryText">
            {numberOfStamps}
          </span>
          <button
            type="button"
            aria-label="Ajouter un timbre"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-primaryText text-primaryText transition hover:bg-primaryText/5"
            onClick={() => setNumberOfStamps((n) => n + 1)}
          >
            <Plus size={16} strokeWidth={2.5} />
          </button>
        </div>
        <p className="mt-3 font-josefin text-lg text-secondaryText">
          Total : {numberOfStamps * PRICE_PER_STAMP} €
        </p>
      </div>
    </LetterDialog>
  );
}

export default BuyStampsModal;
